import useDataset from "datasets/hooks/useDataset";
import { Skeleton } from "~/components/ui/skeleton";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { DatasetAIContext } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetAIContext";
import { DatasetCohortExplorer } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetCohortExplorer";
import { DatasetDashboards } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetDashboards";
import { DatasetFiles } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetFiles";
import { DatasetOverview } from "~/pages/manifold-nextgen/components/datasets/pages/DatasetOverview";
import { useCanViewCohortExplorer } from "~/pages/manifold-nextgen/hooks/useCanViewCohortExplorer";
import { useCanViewDatasetContext } from "~/pages/manifold-nextgen/hooks/useCanViewDatasetContext";
import { nextGenDatasetDetailPath } from "~/router/constants";
import { SessionContextDatasetTables } from "./SessionContextDatasetTables";
import { SessionContextDetailHeader, SessionContextViewOption } from "./SessionContextDetailHeader";

const DATASET_VIEWS: SessionContextViewOption[] = [
  { value: "overview", label: "Overview" },
  { value: "dashboards", label: "Dashboards" },
  { value: "tables", label: "Tables" },
  { value: "files", label: "Files" },
];

const AI_CONTEXT_VIEW: SessionContextViewOption = {
  value: "ai-context",
  label: "Context",
};

const COHORT_EXPLORER_VIEW: SessionContextViewOption = {
  value: "cohort-explorer",
  label: "Cohort Explorer",
};

interface SessionContextDatasetDetailProps {
  datasetId: string;
  view: string;
  onBack: () => void;
  onCollapse: () => void;
  onSetView: (view: string) => void;
}

export const SessionContextDatasetDetail = ({
  datasetId,
  view,
  onBack,
  onCollapse,
  onSetView,
}: SessionContextDatasetDetailProps) => {
  const { data: dataset, error, isLoading } = useDataset(datasetId);
  const showCohortExplorer = useCanViewCohortExplorer(dataset);
  const showAiContext = useCanViewDatasetContext(dataset);

  const views = [
    ...DATASET_VIEWS,
    ...(showAiContext ? [AI_CONTEXT_VIEW] : []),
    ...(showCohortExplorer ? [COHORT_EXPLORER_VIEW] : []),
  ];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SessionContextDetailHeader
        entityName={dataset?.title ?? "…"}
        linkTo={nextGenDatasetDetailPath(datasetId)}
        onBack={onBack}
        onCollapse={onCollapse}
        views={views}
        currentView={view}
        onSetView={onSetView}
      />

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex flex-col gap-3" role="status" aria-label="Loading dataset">
            <Skeleton className="h-8 w-2/3 rounded-md" />
            <Skeleton className="h-32 rounded-lg" />
          </div>
        ) : error || !dataset ? (
          <ResourceLoadError error={error} resourceLabel="dataset" className="py-16" />
        ) : (
          <>
            {view === "overview" && <DatasetOverview dataset={dataset} sessionSidebarView />}
            {view === "dashboards" && <DatasetDashboards dataset={dataset} sessionSidebarView />}
            {view === "tables" && <SessionContextDatasetTables dataset={dataset} />}
            {view === "files" && <DatasetFiles dataset={dataset} sessionSidebarView />}
            {view === "ai-context" && showAiContext && (
              <DatasetAIContext datasetId={dataset.id} sessionSidebarView />
            )}
            {view === "cohort-explorer" && showCohortExplorer && (
              <DatasetCohortExplorer datasetId={dataset.id} sessionSidebarView />
            )}
          </>
        )}
      </div>
    </div>
  );
};
